import React, { useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { 
    Scale, 
    FileText, 
    ShieldCheck, 
    Award, 
    Briefcase, 
    AlertTriangle, 
    Building2, 
    Mail, 
    Phone, 
    MapPin, 
    ArrowLeft, 
    Clock, 
    Calendar, 
    ChevronRight,
    CheckCircle2,
    BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';

function TerminosCondiciones() {
    const [activeSection, setActiveSection] = useState('aceptacion');

    const sections = [
        { id: 'aceptacion', title: '1. Aceptación de los Términos', icon: <CheckCircle2 size={18} /> },
        { id: 'objeto', title: '2. Objeto y Alcance del Sitio', icon: <Building2 size={18} /> },
        { id: 'uso', title: '3. Uso Aceptable y Obligaciones', icon: <FileText size={18} /> },
        { id: 'catalogo', title: '4. Catálogo y Disponibilidad', icon: <BookOpen size={18} /> },
        { id: 'distribuidor', title: '5. Programa de Distribuidores', icon: <Briefcase size={18} /> },
        { id: 'propiedad', title: '6. Propiedad Intelectual y Marcas', icon: <Award size={18} /> },
        { id: 'responsabilidad', title: '7. Limitación de Responsabilidad', icon: <AlertTriangle size={18} /> },
        { id: 'enlaces', title: '8. Enlaces a Plataformas Externas', icon: <ShieldCheck size={18} /> },
        { id: 'modificaciones', title: '9. Modificaciones a los Términos', icon: <Calendar size={18} /> },
        { id: 'jurisdiccion', title: '10. Ley y Jurisdicción Boliviana', icon: <Scale size={18} /> },
        { id: 'contacto', title: '11. Atención y Contacto Legal', icon: <Mail size={18} /> },
    ];

    const scrollToSection = (id) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="legal-page-wrapper">
            {/* Cabecera Hero */}
            <section className="legal-hero-section">
                <div className="legal-hero-background">
                    <div className="legal-shape legal-shape-1"></div>
                    <div className="legal-shape legal-shape-2"></div>
                    <div className="legal-hero-grid"></div>
                </div>

                <Container className="legal-hero-container">
                    <div className="legal-breadcrumb">
                        <Link to="/" className="legal-back-link">
                            <ArrowLeft size={16} /> Volver al Inicio
                        </Link>
                    </div>

                    <div className="legal-hero-badge">
                        <Scale size={20} />
                        <span>Marco Legal y Condiciones de Servicio</span>
                    </div>

                    <h1 className="legal-hero-title">
                        Términos y <span className="title-highlight">Condiciones</span>
                    </h1>

                    <p className="legal-hero-description">
                        Bienvenido al portal web oficial de <strong>Salcedo Importaciones S.R.L.</strong> Al navegar, 
                        interactuar o utilizar nuestros canales digitales, aceptas regirte por los siguientes 
                        términos y condiciones aplicables a nuestros servicios comerciales y de distribución en Bolivia.
                    </p>

                    <div className="legal-meta-info">
                        <span className="meta-item">
                            <Calendar size={16} /> Última actualización: Septiembre 2026
                        </span>
                        <span className="meta-divider">•</span>
                        <span className="meta-item">
                            <Clock size={16} /> Tiempo estimado de lectura: 7 min
                        </span>
                    </div>
                </Container>
            </section>

            {/* Tarjetas resumen */}
            <section className="legal-highlights-section">
                <Container>
                    <Row className="g-4">
                        <Col md={4}>
                            <div className="legal-highlight-card">
                                <div className="highlight-icon-box">
                                    <Award size={24} />
                                </div>
                                <h4>Marcas Oficiales</h4>
                                <p>Distribución garantizada de herramientas líderes: Truper®, Pretul®, Foset®, Volteck®, Fiero®, Hermex® y Klintek®.</p>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="legal-highlight-card">
                                <div className="highlight-icon-box">
                                    <Briefcase size={24} />
                                </div>
                                <h4>Canal de Distribución</h4>
                                <p>Procesos transparentes para registro de socios comerciales y ventas mayoristas en todo el territorio nacional.</p>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="legal-highlight-card">
                                <div className="highlight-icon-box">
                                    <Scale size={24} />
                                </div>
                                <h4>Jurisdicción Legal</h4>
                                <p>Términos regulados conforme a la normativa comercial y civil del Estado Plurinacional de Bolivia.</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Contenido Principal con Sidebar de Navegación */}
            <section className="legal-content-section py-5">
                <Container>
                    <Row className="g-4">
                        {/* Sidebar / Tabla de Contenidos */}
                        <Col lg={4} className="d-none d-lg-block">
                            <div className="legal-sidebar-sticky">
                                <Card className="legal-toc-card">
                                    <Card.Header className="legal-toc-header">
                                        <FileText size={18} />
                                        <span>Índice de Términos</span>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <ul className="legal-toc-list">
                                            {sections.map((sec) => (
                                                <li key={sec.id}>
                                                    <button
                                                        className={`legal-toc-btn ${activeSection === sec.id ? 'active' : ''}`}
                                                        onClick={() => scrollToSection(sec.id)}
                                                    >
                                                        <span className="btn-icon">{sec.icon}</span>
                                                        <span className="btn-text">{sec.title}</span>
                                                        <ChevronRight size={14} className="arrow" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card.Body>
                                </Card>

                                <Card className="legal-help-card mt-4">
                                    <Card.Body>
                                        <h5>¿Consultas Comerciales?</h5>
                                        <p>Comunícate con nuestro departamento de atención al cliente y asesoría técnica.</p>
                                        <a href="mailto:atencionalcliente@salcedoimport.com" className="btn-legal-contact">
                                            <Mail size={16} /> Contactar a la Empresa
                                        </a>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>

                        {/* Texto Legal */}
                        <Col lg={8}>
                            <div className="legal-document-body">
                                
                                {/* Sección 1 */}
                                <div id="aceptacion" className="legal-block">
                                    <div className="legal-block-header">
                                        <CheckCircle2 className="block-icon" size={24} />
                                        <h2>1. Aceptación de los Términos y Condiciones</h2>
                                    </div>
                                    <p>
                                        El acceso, uso, visualización o envío de información a través del sitio web 
                                        <strong> www.salcedoimport.com</strong> atribuye la condición de Usuario a quien 
                                        lo realice y expresa la aceptación plena, libre e incondicional de todos y cada 
                                        uno de los presentes Términos y Condiciones.
                                    </p>
                                    <p>
                                        Si no estás de acuerdo con alguna de las estipulaciones aquí descritas, te rogamos 
                                        abstenerte de hacer uso de este sitio web o de remitir formularios de contacto.
                                    </p>
                                </div>

                                {/* Sección 2 */}
                                <div id="objeto" className="legal-block">
                                    <div className="legal-block-header">
                                        <Building2 className="block-icon" size={24} />
                                        <h2>2. Objeto y Alcance del Sitio Web</h2>
                                    </div>
                                    <p>
                                        El presente portal web tiene como objetivo principal brindar información institucional 
                                        y comercial sobre <strong>SALCEDO IMPORTACIONES S.R.L.</strong>, exhibir nuestro catálogo 
                                        de productos de ferretería y herramientas, facilitar la recepción de consultas comerciales 
                                        y gestionar solicitudes de postulación para ser distribuidores autorizados en Bolivia.
                                    </p>
                                </div>

                                {/* Sección 3 */}
                                <div id="uso" className="legal-block">
                                    <div className="legal-block-header">
                                        <FileText className="block-icon" size={24} />
                                        <h2>3. Uso Aceptable y Obligaciones del Usuario</h2>
                                    </div>
                                    <p>
                                        El Usuario se compromete a hacer un uso diligente, correcto y lícito del sitio web. 
                                        Queda terminantemente prohibido:
                                    </p>
                                    <ul className="legal-bullet-list">
                                        <li>Proporcionar información falsa, engañosa o suplantar la identidad de terceros en los formularios de contacto o distribución.</li>
                                        <li>Utilizar el sitio web con fines fraudulentos, ilegales o lesivos contra los derechos e intereses de Salcedo Importaciones S.R.L. o de terceros.</li>
                                        <li>Introducir virus, malware, scripts automatizados o cualquier sistema tecnológico perjudicial para los servidores del sitio.</li>
                                        <li>Intentar vulnerar los mecanismos de seguridad, autenticación o configuración de red de la plataforma.</li>
                                    </ul>
                                </div>

                                {/* Sección 4 */}
                                <div id="catalogo" className="legal-block">
                                    <div className="legal-block-header">
                                        <BookOpen className="block-icon" size={24} />
                                        <h2>4. Catálogo de Productos, Precios e Imágenes</h2>
                                    </div>
                                    <p>
                                        Las imágenes, descripciones, especificaciones técnicas, líneas de producto y precios 
                                        mostrados en el catálogo digital o showroom tienen carácter referencial y orientativo.
                                    </p>
                                    <div className="legal-sub-box">
                                        <p>
                                            Salcedo Importaciones S.R.L. realiza sus mejores esfuerzos para mantener actualizada 
                                            la información de disponibilidad de stock y especificaciones; sin embargo, los 
                                            precios finales de venta mayorista y minorista están sujetos a confirmación mediante 
                                            cotización formal y pueden variar sin previo aviso de acuerdo a condiciones de importación o aranceles.
                                        </p>
                                    </div>
                                </div>

                                {/* Sección 5 */}
                                <div id="distribuidor" className="legal-block">
                                    <div className="legal-block-header">
                                        <Briefcase className="block-icon" size={24} />
                                        <h2>5. Programa de Distribuidores y Solicitudes Comerciales</h2>
                                    </div>
                                    <p>
                                        El envío del formulario "Ser Distribuidor" constituye una solicitud comercial de 
                                        interés y <strong>no genera un compromiso contractual vinculante inmediato</strong> de 
                                        distribución, concesión exclusiva o relación laboral entre el solicitante y 
                                        Salcedo Importaciones S.R.L.
                                    </p>
                                    <p>
                                        Nuestro equipo comercial se reserva el derecho de evaluar los antecedentes comerciales, 
                                        capacidad operativa y ubicación geográfica de cada postulante para determinar la 
                                        viabilidad de la apertura de cuenta mayorista.
                                    </p>
                                </div>

                                {/* Sección 6 */}
                                <div id="propiedad" className="legal-block">
                                    <div className="legal-block-header">
                                        <Award className="block-icon" size={24} />
                                        <h2>6. Propiedad Intelectual y Marcas Registradas</h2>
                                    </div>
                                    <p>
                                        Todos los contenidos de este sitio web (diseños gráficos, logotipos, código fuente, 
                                        textos, fotografías, catálogos PDF, iconos y marcas registradas) son propiedad exclusiva 
                                        de <strong>Salcedo Importaciones S.R.L.</strong> o de sus respectivos fabricantes y 
                                        titulares de marca que nos han otorgado autorización expresa de distribución:
                                    </p>
                                    <ul className="legal-info-list">
                                        <li><strong>Truper®</strong> – Herramientas manuales y eléctricas de alta exigencia.</li>
                                        <li><strong>Pretul®</strong> – Herramientas de uso doméstico y ligero.</li>
                                        <li><strong>Foset®</strong> – Grifería, tuberías y soluciones en plomería.</li>
                                        <li><strong>Volteck®</strong> – Material y componentes eléctricos.</li>
                                        <li><strong>Fiero®</strong> – Cerrajería, mallas y productos de fijación en acero.</li>
                                        <li><strong>Hermex®</strong> – Candados, chapas y seguridad.</li>
                                        <li><strong>Klintek®</strong> – Productos de limpieza industrial y mantenimiento.</li>
                                    </ul>
                                    <p>
                                        Queda prohibida su reproducción, copia, distribución o comercialización sin la autorización 
                                        previa y por escrito de sus legítimos titulares.
                                    </p>
                                </div>

                                {/* Sección 7 */}
                                <div id="responsabilidad" className="legal-block">
                                    <div className="legal-block-header">
                                        <AlertTriangle className="block-icon" size={24} />
                                        <h2>7. Limitación de Responsabilidad</h2>
                                    </div>
                                    <p>
                                        Salcedo Importaciones S.R.L. no será responsable por:
                                    </p>
                                    <ul className="legal-bullet-list">
                                        <li>Interrupciones temporales o caídas imprevistas del servicio causadas por proveedores de internet o mantenimiento de servidores.</li>
                                        <li>Uso indebido o negligente que terceros hagan de los datos o herramientas exhibidas en la web.</li>
                                        <li>Daños o perjuicios derivados de virus o programas maliciosos transmitidos por factores ajenos al control de la Empresa.</li>
                                    </ul>
                                </div>

                                {/* Sección 8 */}
                                <div id="enlaces" className="legal-block">
                                    <div className="legal-block-header">
                                        <ShieldCheck className="block-icon" size={24} />
                                        <h2>8. Enlaces a Sitios y Redes Sociales de Terceros</h2>
                                    </div>
                                    <p>
                                        El sitio web puede contener enlaces directos a plataformas externas (como WhatsApp, Facebook, 
                                        Instagram, TikTok, YouTube o Google Maps). Salcedo Importaciones S.R.L. no asume responsabilidad 
                                        alguna sobre las políticas de privacidad, contenidos o condiciones de uso de dichos sitios web externos.
                                    </p>
                                </div>

                                {/* Sección 9 */}
                                <div id="modificaciones" className="legal-block">
                                    <div className="legal-block-header">
                                        <Calendar className="block-icon" size={24} />
                                        <h2>9. Modificaciones de los Términos y Condiciones</h2>
                                    </div>
                                    <p>
                                        La Empresa se reserva la potestad de actualizar, modificar o reemplazar cualquiera de 
                                        las cláusulas de estos Términos y Condiciones en cualquier momento. La vigencia de las 
                                        modificaciones comenzará a partir de su publicación en el sitio web.
                                    </p>
                                </div>

                                {/* Sección 10 */}
                                <div id="jurisdiccion" className="legal-block">
                                    <div className="legal-block-header">
                                        <Scale className="block-icon" size={24} />
                                        <h2>10. Ley Aplicable y Jurisdicción</h2>
                                    </div>
                                    <p>
                                        Los presentes Términos y Condiciones se rigen e interpretan en su totalidad por las 
                                        leyes vigentes del <strong>Estado Plurinacional de Bolivia</strong> (incluyendo el Código de Comercio y 
                                        normas civiles concordantes). Para la resolución de cualquier discrepancia, controversia 
                                        o litigio que pudiera suscitarse, las partes se someten voluntariamente a la jurisdicción 
                                        de los tribunales competentes de la ciudad de <strong>Cochabamba, Bolivia</strong>.
                                    </p>
                                </div>

                                {/* Sección 11 */}
                                <div id="contacto" className="legal-block legal-contact-block">
                                    <div className="legal-block-header">
                                        <Mail className="block-icon" size={24} />
                                        <h2>11. Atención y Contacto Legal</h2>
                                    </div>
                                    <p>
                                        Para cualquier duda o requerimiento legal relativo a los presentes Términos y Condiciones, 
                                        puedes comunicarte a través de nuestros canales oficiales:
                                    </p>
                                    
                                    <div className="legal-contact-grid">
                                        <div className="contact-box">
                                            <Mail size={20} className="icon" />
                                            <div>
                                                <strong>Correo Institucional:</strong>
                                                <p>atencionalcliente@salcedoimport.com</p>
                                            </div>
                                        </div>
                                        <div className="contact-box">
                                            <Phone size={20} className="icon" />
                                            <div>
                                                <strong>Central Telefónica:</strong>
                                                <p>4-544825 | 4-544712 | 75986800</p>
                                            </div>
                                        </div>
                                        <div className="contact-box">
                                            <MapPin size={20} className="icon" />
                                            <div>
                                                <strong>Dirección Legal:</strong>
                                                <p>Av. Villazón Km. 5 Sacaba, Cochabamba - Bolivia</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 text-center">
                                        <Link to="/distribuidor" className="btn-ir-distribuidor">
                                            Postular a Distribuidor Autorizado
                                        </Link>
                                    </div>
                                </div>

                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default TerminosCondiciones;
